import React, { useEffect, useState } from 'react';
import { MapPin, Sparkles } from 'lucide-react';

const TARGET_DATE = new Date('2026-09-01T09:00:00+02:00').getTime();

const calculateTimeLeft = () => {
  const diff = TARGET_DATE - Date.now();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60)
  };
};

const Cell = ({ value, label }) => (
  <div className="flex flex-col items-center min-w-[52px] md:min-w-[64px]">
    <span
      className="font-mono text-2xl md:text-3xl font-bold text-white tabular-nums leading-none tracking-tight"
      data-testid={`countdown-banner-${label.toLowerCase()}`}
    >
      {String(value).padStart(2, '0')}
    </span>
    <span className="mt-1 text-[9px] md:text-[10px] uppercase tracking-[0.15em] text-white/60 font-medium">
      {label}
    </span>
  </div>
);

export const CountdownBanner = () => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  if (!timeLeft) return null;

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-white/10 mt-20"
      data-testid="countdown-banner"
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-5 md:gap-8">
          <div className="flex items-center gap-4 text-center md:text-left">
            <div className="hidden sm:flex flex-shrink-0 w-11 h-11 items-center justify-center rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
              <Sparkles className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                <span className="inline-flex items-center gap-1.5 text-[10px] md:text-xs font-bold uppercase tracking-[0.18em] text-amber-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                  Neueröffnung
                </span>
              </div>
              <h3 className="text-base md:text-lg font-semibold text-white leading-snug">
                Neuer <span className="text-amber-300">Omoda &amp; Jaecoo</span> Standort im Florapark Magdeburg
              </h3>
              <p className="mt-0.5 text-xs md:text-sm text-white/60 flex items-center justify-center md:justify-start gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                Eröffnung am 1. September 2026
              </p>
            </div>
          </div>

          <div className="flex items-end gap-1.5 md:gap-3 px-5 py-3 md:py-2 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
            <Cell value={timeLeft.days} label="Tage" />
            <span className="text-white/30 text-2xl md:text-3xl font-light leading-none pb-4" aria-hidden="true">:</span>
            <Cell value={timeLeft.hours} label="Std" />
            <span className="text-white/30 text-2xl md:text-3xl font-light leading-none pb-4" aria-hidden="true">:</span>
            <Cell value={timeLeft.minutes} label="Min" />
            <span className="text-white/30 text-2xl md:text-3xl font-light leading-none pb-4" aria-hidden="true">:</span>
            <Cell value={timeLeft.seconds} label="Sek" />
          </div>
        </div>
      </div>
    </section>
  );
};
